import React, { useEffect, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { getCurrentUser, getProfile, updateProfile } from '../../utils/auth';
import { Form, Input, Button, Upload, Avatar, message } from 'antd';
import { UploadOutlined } from '@ant-design/icons';
import { getPosts } from '../../utils/storage';
import PostCard from '../../components/PostCard';
import './index.css';

function getBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
    reader.onerror = error => reject(error);
  });
}

// compress and crop to centered square then resize to maxWidth
function compressDataUrl(dataUrl, maxWidth = 800, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const minDim = Math.min(img.width, img.height);
      const canvas = document.createElement('canvas');
      const targetSize = Math.min(maxWidth, minDim);
      canvas.width = targetSize;
      canvas.height = targetSize;
      const ctx = canvas.getContext('2d');
      // crop center square
      const sx = Math.floor((img.width - minDim) / 2);
      const sy = Math.floor((img.height - minDim) / 2);
      ctx.drawImage(img, sx, sy, minDim, minDim, 0, 0, targetSize, targetSize);
      const newDataUrl = canvas.toDataURL('image/jpeg', quality);
      resolve(newDataUrl);
    };
    img.onerror = (e) => reject(e);
    img.src = dataUrl;
  });
}

export default function ProfilePage() {
  const cur = getCurrentUser();
  const username = cur?.username;
  const [form] = Form.useForm();
  const [avatar, setAvatar] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [posts, setPosts] = useState([]);

  function loadPosts() {
    if (!username) return setPosts([]);
    const all = getPosts();
    setPosts(all.filter(p => p.author === username));
  }

  useEffect(() => {
    loadPosts();
    const onStorage = () => loadPosts();
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  useEffect(() => {
    if (!username) navigate('/login');
    const p = getProfile(username) || { displayName: '', bio: '', website: '', avatarUrl: '' };
    form.setFieldsValue({ displayName: p.displayName, bio: p.bio, website: p.website });
    setAvatar(p.avatarUrl || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [username]);

  async function onFinish(values) {
    try {
      setLoading(true);
      const payload = { ...values, avatarUrl: avatar };
      const res = updateProfile(username, payload);
      if (!res.ok) throw new Error(res.error || '保存失败');
      message.success('保存成功');
    } catch (err) {
      message.error('保存失败：' + err.message);
    } finally {
      setLoading(false);
    }
  }

  async function beforeUpload(file) {
    // limit file size to 2MB
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error('图片大小应小于 2MB');
      return Upload.LIST_IGNORE;
    }
    const dataUrl = await getBase64(file);
    // compress (resize + convert to jpeg)
    try {
      const compressed = await compressDataUrl(dataUrl, 800, 0.8);
      setAvatar(compressed);
      message.success('图片已压缩并设置为头像');
    } catch (err) {
      // fallback to original
      setAvatar(dataUrl);
      message.warning('无法压缩图片，已使用原图');
    }
    return Upload.LIST_IGNORE; // prevent auto upload
  }

  return (
    <div className="container">
      <h1>我的资料</h1>
      <div className="profile-form">
        <Form form={form} layout="vertical" onFinish={onFinish}>
          <Form.Item label="头像">
            <div className="avatar-wrapper">
              <Avatar src={avatar || '/avatar-placeholder.svg'} size={96} />
              <div className="avatar-actions">
                <Upload beforeUpload={beforeUpload} showUploadList={false} accept="image/*">
                  <Button icon={<UploadOutlined />}>上传头像</Button>
                </Upload>
                <Button className="ant-btn" onClick={() => { setAvatar(''); message.info('已重置头像'); }}>重置头像</Button>
              </div>
            </div>
            <div className="field-note">上传后的头像会被裁剪为正方形并压缩至合适大小（最多 2MB）</div>
          </Form.Item>

          <div className="profile-grid">
            <Form.Item name="displayName" label="显示名称" rules={[{ required: true, message: '请填写显示名称' }]}> 
              <Input placeholder="显示名称" />
            </Form.Item>

            <Form.Item
              name="website"
              label="个人网站"
              rules={[{ validator: (_, value) => {
                if (!value) return Promise.resolve();
                return /^https?:\/\//i.test(value) ? Promise.resolve() : Promise.reject('请输入以 http:// 或 https:// 开头的 URL');
              } }]}
            >
              <Input placeholder="https://your-site.example" />
            </Form.Item>
          </div>

          <Form.Item name="bio" label="个人简介">
            <Input.TextArea rows={3} placeholder="一句话介绍自己" />
          </Form.Item>

          <Form.Item>
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="primary" htmlType="submit" loading={loading}>保存资料</Button>
            </div>
          </Form.Item>
        </Form>
        <div style={{ marginTop: 12 }}><Link to="/settings">想修改密码？到「设置」页面操作</Link></div>

      </div>

      <div className="container" style={{ maxWidth: 820, marginTop: 18 }}>
        <h2>我的文章 ({posts.length})</h2>
        <div style={{ background: 'transparent', marginTop: 8 }}>
          {posts.length === 0 ? (
            <div className="empty">你还没有发布过文章，<Link to="/new">去写一篇</Link></div>
          ) : (
            posts.map(p => <PostCard key={p.id} post={p} />)
          )}
        </div>
      </div>
    </div>
  );
}
